import { useBuffered } from './useBuffered';
import { Fragment, type ReactNode } from 'react';
import { de, sg } from '../engine/util';
import { PC, PA } from '../engine/rules';
import { GENDER } from '../content/ressorts';

export function Seg({ value, options, onChange, disabled }: { value: any; options: [any, ReactNode][]; onChange: (v: any) => void; disabled?: boolean }) {
  return (
    <div className="seg">
      {options.map(([v, l]) => (
        <button key={String(v)} type="button" aria-pressed={String(value) === String(v)} disabled={disabled} onClick={() => onChange(v)}>{l}</button>
      ))}
    </div>
  );
}

export function Panel({ title, aside, children, className }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={'panel ' + (className || '')}>
      {title && <h3>{title}{aside && <span className="label">{aside}</span>}</h3>}
      {children}
    </div>
  );
}

const ICONS: Record<string, ReactNode> = {
  poll: <path d="M3 20h18M6 16V9M11 16V4M16 16v-6M21 16v-3" />,
  appr: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-5-6.3" /></>,
  budget: <><rect x="3" y="7" width="18" height="12" rx="1.5" /><circle cx="12" cy="13" r="3" /><path d="M3 11h2M19 15h2" /></>,
  debt: <path d="M3 10l9-6 9 6M5 10v9M9 10v9M15 10v9M19 10v9M3 19h18" />,
  gr: <path d="M3 17l6-6 4 4 8-8M15 7h6v6" />,
  al: <><rect x="3" y="7" width="18" height="13" rx="1.5" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" /></>,
  infl: <path d="M12 3c1 4 5 6 5 11a5 5 0 0 1-10 0c0-3 2-4 2-6 1 1 2 2 3 2 0-3-1-5 0-7z" />,
};
export function Icon({ k }: { k: string }) { return <span className="ic" aria-hidden="true"><svg viewBox="0 0 24 24">{ICONS[k]}</svg></span>; }

export function Stat({ label, value, delta, goodUp = true, unit = '', icon, sub, bad }: { label: ReactNode; value: string; delta?: number | null; goodUp?: boolean; unit?: string; icon?: string; sub?: string; bad?: boolean }) {
  let d: ReactNode = null;
  if (delta != null && Math.abs(delta) >= .05) { const g = goodUp ? delta > 0 : delta < 0; d = <span className={'d ' + (g ? 'up' : 'down')}>{sg(delta)}{unit}</span>; }
  return (
    <div className="stat">{icon && <Icon k={icon} />}<span className="label">{label}</span><span className={'v' + (bad ? ' down' : '')}>{value}{d}{sub && <span className="d">{sub}</span>}</span></div>
  );
}

export function Bar({ value, mid, coal, color, label, wide }: { value: number; mid?: boolean; coal?: boolean; color?: string; label: ReactNode; wide?: boolean }) {
  return (
    <div className={'grp' + (wide ? ' wide' : '')}><span>{label}</span><div className={'bar' + (mid ? ' mid' : '') + (coal ? ' coal' : '')}><i style={{ width: `${value}%`, background: color }} /></div><span className="num">{Math.round(value)}</span></div>
  );
}

export function Tag({ good, children }: { good?: boolean | null; children: ReactNode }) {
  return <span className={'tag ' + (good == null ? '' : good ? 'up' : 'down')}>{children}</span>;
}

export const initials = (n: string) => n.split(' ').map((x) => x[0]).join('');
export function Avatar({ M, size }: { M: any; size?: 'sm' | 'lg' }) {
  if (size === 'sm') return <span className="av sm" style={{ background: PC(M.p) }} aria-hidden="true">{initials(M.n)}</span>;
  return <span className={'av face ' + (size || '')} style={{ background: PC(M.p) }} aria-hidden="true"><Portrait name={M.n} tie={PC(M.p)} /></span>;
}

/** Zahl aus einem Namen, damit dieselbe Person immer gleich aussieht. */
function hashOf(s: string) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
const SKIN = ['#f3cfb0', '#e8b48e', '#d39a6f', '#b07650', '#8a5a3c', '#f6dcc4'];
const HAIR = ['#2a1f18', '#4a3020', '#7a4a26', '#b9894a', '#8f8f8f', '#d9d4ca', '#1c1c1c'];

/** Frisuren: 0 kurz, 1 Seitenscheitel, 2 lang, 3 Dutt, 4 Halbglatze, 5 Locken, 6 Bob, 7 lang mit Scheitel. */
const STYLES = { m: [0, 1, 4, 5, 0, 1], f: [2, 3, 6, 7, 2, 6], x: [0, 1, 2, 3, 5, 6] };
/** Geschlecht aus dem Vornamen (Liste in content/ressorts.ts), unbekannte Namen: beides möglich. */
export const genderOf = (name: string): 'f' | 'm' | 'x' => GENDER[name.split(' ')[0]] ?? 'x';

/** Gezeichnetes Porträt aus Bausteinen: Hautton, Frisur, Haarfarbe, Brille, Bart oder Schmuck, Krawatte oder Kette in Parteifarbe. */
export function Portrait({ name, tie = '#d6aa4e' }: { name: string; tie?: string }) {
  const h = hashOf(name), pick = <T,>(a: T[], k: number) => a[Math.floor(h / k) % a.length];
  const g = genderOf(name), styles = STYLES[g];
  const skin = pick(SKIN, 1), hairC = pick(HAIR, 7), style = styles[Math.floor(h / 53) % styles.length], glasses = (h >> 9) % 10 < 3;
  const fem = g === 'f' || (g === 'x' && [2, 3, 6].includes(style));
  const beard = !fem && style !== 4 && (h >> 13) % 10 < 2, earrings = fem && (h >> 11) % 10 < 6;
  const grey = style === 4 ? '#8f8f8f' : hairC;
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%">
      {style === 7 && <path d="M10 18c0-8 4-12 10-12s10 4 10 12v13c-2 1.4-4.4 1.4-6 0V18H16v13c-1.6 1.4-4 1.4-6 0z" fill={grey} />}
      <path d="M6 40c1-8 7-12 14-12s13 4 14 12z" fill="#1d252b" />
      {fem
        ? <><path d="M16 28l4 7 4-7z" fill="#e9e4da" /><path d="M17.4 29.4c.8 2 1.6 3 2.6 3s1.8-1 2.6-3" stroke={tie} strokeWidth=".7" fill="none" /><circle cx="20" cy="32.6" r=".9" fill={tie} /></>
        : <><path d="M16 28l4 6 4-6z" fill="#e9e4da" /><path d="M19.2 30l.8 8 .8-8-.8-1.2z" fill={tie} /></>}
      <rect x="17" y="22" width="6" height="6" rx="2" fill={skin} />
      {style === 2 && <path d="M10 18c0-8 4-12 10-12s10 4 10 12v10c-2 1-4 1-5 0V18H15v10c-1 1-3 1-5 0z" fill={grey} />}
      {style === 6 && <path d="M11 17c0-7 4-11 9-11s9 4 9 11v7.5c-1.6.9-3.2.9-4.4 0V18h-9.2v6.5c-1.2.9-2.8.9-4.4 0z" fill={grey} />}
      <ellipse cx="20" cy="17" rx="7" ry="8.4" fill={skin} />
      {style === 0 && <path d="M13 15c0-5 3-8 7-8s7 3 7 8c-1-2-3-3.5-7-3.5S14 13 13 15z" fill={grey} />}
      {style === 1 && <path d="M12.8 16c-.5-6 3-9.5 7.5-9.5 4.8 0 7.4 3.4 6.9 9-1-3-3-4.6-6-4.6-2 0-3 1-5 1-1.5 0-2.6 1.4-3.4 4.1z" fill={grey} />}
      {style === 2 && <path d="M12.6 16.5c-.4-6.5 3.2-10 7.4-10s7.8 3.5 7.4 10c-1.6-3.2-4-4.6-7.4-4.6s-5.8 1.4-7.4 4.6z" fill={grey} />}
      {style === 3 && <><circle cx="20" cy="6.4" r="3.2" fill={grey} /><path d="M12.8 16c-.3-5.6 3-9 7.2-9s7.5 3.4 7.2 9c-1.4-3-3.8-4.4-7.2-4.4s-5.8 1.4-7.2 4.4z" fill={grey} /></>}
      {style === 4 && <path d="M12.6 17c0-3 .6-4.6 1.4-5.2.2 1.6.4 2.6.4 4.6zM27.4 17c0-3-.6-4.6-1.4-5.2-.2 1.6-.4 2.6-.4 4.6z" fill={grey} />}
      {style === 5 && <path d="M12.4 15.5c-1-5 2-9.5 7.6-9.5s8.6 4.5 7.6 9.5c-.6-.8-1-1.8-1.4-2.6-.8 1-1.6.4-2.2-.4-.8.8-1.8.8-2.4 0-.8.8-2 .8-2.6 0-.6.8-1.6 1.2-2.4.4-.6 1-1.4 1.6-2.2 1-.4.8-1.2 1.4-2 1.6z" fill={grey} />}
      {style === 6 && <path d="M12.4 17.5c-.6-6.8 3-10.6 7.6-10.6s8.2 3.8 7.6 10.6c-.8-2.6-1.8-4.2-3-5-1.8 1-4.6 1.4-8 .8-1.8.8-3.2 2.2-4.2 4.2z" fill={grey} />}
      {style === 7 && <path d="M12.4 17c-.6-6.6 3-10.4 7.6-10.4s8.2 3.8 7.6 10.4c-1-3.6-3.2-5.6-6.4-6-2.8 1.6-5.8 2.8-8.8 6z" fill={grey} />}
      <circle cx="17.2" cy="17.4" r=".9" fill="#2a211b" /><circle cx="22.8" cy="17.4" r=".9" fill="#2a211b" />
      <path d="M18 21.6c1.2.8 2.8.8 4 0" stroke={fem ? '#9a4a4a' : '#7a4a3a'} strokeWidth=".8" fill="none" strokeLinecap="round" />
      {beard && <path d="M13.4 19.5c.4 4.6 3 7.5 6.6 7.5s6.2-2.9 6.6-7.5c-1 1.6-2 2.6-3.2 3-1-.8-2.2-1-3.4-1s-2.4.2-3.4 1c-1.2-.4-2.2-1.4-3.2-3z" fill={grey} opacity=".9" />}
      {earrings && <><circle cx="13.1" cy="20" r=".75" fill="#e6c36a" /><circle cx="26.9" cy="20" r=".75" fill="#e6c36a" /></>}
      {glasses && <g fill="none" stroke="#1c1c1c" strokeWidth=".8"><circle cx="17.2" cy="17.4" r="2.3" /><circle cx="22.8" cy="17.4" r="2.3" /><path d="M19.5 17.4h1" /></g>}
    </svg>
  );
}
export function AvatarP({ id }: { id: string }) { return <span className="av" style={{ background: PC(id) }} aria-hidden="true">{PA(id)}</span>; }
export function Sw({ id }: { id: string }) { return <span className="sw" style={{ background: PC(id) }} />; }

export function Dots({ n }: { n: number }) { return <span className="dots" aria-label={`${n} von 5`}>{'●'.repeat(n)}<span>{'●'.repeat(5 - n)}</span></span>; }

export function Range({ id, label, value, min, max, step, unit, d, onChange }: { id: string; label: string; value: number; min: number; max: number; step: number; unit: string; d?: number; onChange: (v: number) => void }) {
  const [v, setV, flush] = useBuffered(value, onChange, 250);
  return (
    <div className="param">
      <label className="pl" htmlFor={id}><span>{label}</span><span className="num">{de(v, d ?? (step < 1 ? 1 : 0))}{unit}</span></label>
      <input type="range" id={id} min={min} max={max} step={step} value={v} onChange={(e) => setV(+e.target.value)} onPointerUp={flush} onBlur={flush} />
    </div>
  );
}

export function KV({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return <div className="kv">{rows.map(([k, v], i) => <Fragment key={i}><span>{k}</span><b className="num">{v}</b></Fragment>)}</div>;
}

export function Box({ label, children, warn }: { label: ReactNode; children: ReactNode; warn?: boolean }) {
  return <div className={'box' + (warn ? ' gg' : '')}><span className="label">{label}</span>{children}</div>;
}
